import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { applicationApi, rejectionReasonApi, Application } from '../services/recruitingApi';
import type { CandidateNotificationStatus } from '../services/recruitingApi';
import { jobKeys } from './useJobs';

export interface ListApplicationsParams {
  stage_id?: string;
  status?: string;
  owner_id?: string;
  limit?: number;
  offset?: number;
}

export interface ApplicationCreate {
  candidate_id: string;
  job_posting_id: string;
  owner_id?: string;
  cover_letter?: string;
  application_answers?: Record<string, unknown>;
}

export const applicationKeys = {
  all: ['applications'] as const,
  byJob: (jobId: string, params?: ListApplicationsParams) =>
    [...applicationKeys.all, 'job', jobId, params] as const,
  byCandidate: (candidateId: string) => [...applicationKeys.all, 'candidate', candidateId] as const,
  detail: (id: string) => [...applicationKeys.all, 'detail', id] as const,
  timeline: (id: string) => [...applicationKeys.all, 'timeline', id] as const,
};

export function useApplicationsByJob(jobId: string, params?: ListApplicationsParams) {
  return useQuery({
    queryKey: applicationKeys.byJob(jobId, params),
    queryFn: () => applicationApi.listByJob(jobId, params),
    enabled: !!jobId,
  });
}

export function useApplicationsByCandidate(candidateId: string) {
  return useQuery({
    queryKey: applicationKeys.byCandidate(candidateId),
    queryFn: () => applicationApi.listByCandidate(candidateId),
    enabled: !!candidateId,
  });
}

export function useApplication(id: string) {
  return useQuery({
    queryKey: applicationKeys.detail(id),
    queryFn: () => applicationApi.get(id),
    enabled: !!id,
  });
}

export function useApplicationTimeline(id: string) {
  return useQuery({
    queryKey: applicationKeys.timeline(id),
    queryFn: () => applicationApi.getTimeline(id),
    enabled: !!id,
  });
}

export function useCreateApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ApplicationCreate) => applicationApi.create(data),
    onSuccess: (application: Application) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.byJob(application.job_posting_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byCandidate(application.candidate_id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useMoveApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) => applicationApi.moveToStage(id, stageId),
    onSuccess: (application: Application) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(application.id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byJob(application.job_posting_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.timeline(application.id) });
    },
  });
}

export function useRejectionReasons() {
  return useQuery({
    queryKey: ['rejectionReasons'],
    queryFn: () => rejectionReasonApi.list(),
  });
}

export function useRejectApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rejection_reason_id,
      rejection_notes,
      candidate_notification_status,
    }: {
      id: string;
      rejection_reason_id?: string;
      rejection_notes?: string;
      candidate_notification_status?: CandidateNotificationStatus;
    }) => applicationApi.reject(id, { rejection_reason_id, rejection_notes, candidate_notification_status }),
    onSuccess: (application: Application) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(application.id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byJob(application.job_posting_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byCandidate(application.candidate_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.timeline(application.id) });
    },
  });
}

export function useHireApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      offer_salary,
      offer_currency,
      offer_start_date,
      candidate_notification_status,
    }: {
      id: string;
      offer_salary?: number;
      offer_currency?: string;
      offer_start_date?: string;
      candidate_notification_status?: CandidateNotificationStatus;
    }) => applicationApi.hire(id, { offer_salary, offer_currency, offer_start_date, candidate_notification_status }),
    onSuccess: (application: Application) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(application.id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byJob(application.job_posting_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byCandidate(application.candidate_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.timeline(application.id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(application.job_posting_id) });
    },
  });
}

export function useWithdrawApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => applicationApi.withdraw(id),
    onSuccess: (application: Application) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(application.id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byJob(application.job_posting_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.byCandidate(application.candidate_id) });
      queryClient.invalidateQueries({ queryKey: applicationKeys.timeline(application.id) });
    },
  });
}

